import { createApi } from "@reduxjs/toolkit/query/react";
import { components } from "../../types/api";
import { baseQueryWithReauth } from "../../utils/baseQuery";

// Define types for custom entity operations
type CreateCustomEntityNameRequest =
  components["schemas"]["CreateCustomEntityNameRequest"];
type UpdateCustomEntityNameRequest =
  components["schemas"]["UpdateCustomEntityNameRequest"];
type ApiResponseCustomEntityName =
  components["schemas"]["ApiResponse_CustomEntityName_"];
type ApiResponseCustomEntityNameWithOriginal =
  components["schemas"]["ApiResponse_CustomEntityNameWithOriginal_"];
type ApiResponseCustomEntityNameWithOriginalArray =
  components["schemas"]["ApiResponse_CustomEntityNameWithOriginal-Array_"];
type ApiResponseDisplayName =
  components["schemas"]["ApiResponse__displayName-string__"];

// Bulk upsert types
type BulkCustomEntityNameItem =
  components["schemas"]["BulkCustomEntityNameItem"];
type ApiResponseBulkUpsertResult =
  components["schemas"]["ApiResponse_BulkUpsertResult_"];
type EntityType = components["schemas"]["EntityType"];

export const customEntitiesApi = createApi({
  reducerPath: "customEntitiesApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["CustomEntityNames"],
  endpoints: (builder) => ({
    // Create a custom name for an entity
    createCustomEntityName: builder.mutation<
      ApiResponseCustomEntityName,
      CreateCustomEntityNameRequest
    >({
      query: (newCustomEntityName) => ({
        url: "/custom-entities/names",
        method: "POST",
        body: newCustomEntityName,
      }),
      invalidatesTags: ["CustomEntityNames"],
    }),

    // Get custom name for a specific entity
    getCustomEntityName: builder.query<
      ApiResponseCustomEntityNameWithOriginal,
      { entityType: string; entityId: string }
    >({
      query: ({ entityType, entityId }) =>
        `/custom-entities/names/${entityType}/${entityId}`,
      providesTags: ["CustomEntityNames"],
    }),

    // Update a custom entity name
    updateCustomEntityName: builder.mutation<
      ApiResponseCustomEntityName,
      {
        entityType: string;
        entityId: string;
        data: UpdateCustomEntityNameRequest;
      }
    >({
      query: ({ entityType, entityId, data }) => ({
        url: `/custom-entities/names/${entityType}/${entityId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["CustomEntityNames"],
    }),

    // Delete a custom entity name (revert to original name)
    deleteCustomEntityName: builder.mutation<
      void,
      { entityType: string; entityId: string }
    >({
      query: ({ entityType, entityId }) => ({
        url: `/custom-entities/names/${entityType}/${entityId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CustomEntityNames"],
    }),

    // Get all custom entity names for a specific entity type
    getCustomEntityNamesByType: builder.query<
      ApiResponseCustomEntityNameWithOriginalArray,
      string
    >({
      query: (entityType) => `/custom-entities/names/${entityType}`,
      providesTags: ["CustomEntityNames"],
    }),

    // Get the effective display name for an entity (custom name if exists, otherwise original)
    getEffectiveDisplayName: builder.query<
      ApiResponseDisplayName,
      { entityType: string; entityId: string }
    >({
      query: ({ entityType, entityId }) =>
        `/custom-entities/display-name/${entityType}/${entityId}`,
      providesTags: ["CustomEntityNames"],
    }),

    // Bulk create/update custom names for entities of a given type
    bulkUpsertCustomEntityNames: builder.mutation<
      ApiResponseBulkUpsertResult,
      { entityType: EntityType; items: BulkCustomEntityNameItem[] }
    >({
      query: ({ entityType, items }) => ({
        url: `/custom-entities/names/bulk/${entityType}`,
        method: "POST",
        body: { items },
      }),
      invalidatesTags: ["CustomEntityNames"],
    }),
  }),
});

export const {
  useCreateCustomEntityNameMutation,
  useGetCustomEntityNameQuery,
  useUpdateCustomEntityNameMutation,
  useDeleteCustomEntityNameMutation,
  useBulkUpsertCustomEntityNamesMutation,
  useGetCustomEntityNamesByTypeQuery,
  useGetEffectiveDisplayNameQuery,
} = customEntitiesApi;
